import { useParams, Link } from "@/lib/router-compat";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import PageLoading from "@/components/PageLoading";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Helmet } from "react-helmet-async";
import { isThinProduct, jsonLd } from "@/lib/seoHelpers";
import {
  Star, ExternalLink, CheckCircle2, XCircle, ShoppingCart, ChevronRight, Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { buildProductSchema, dedupeAffiliatesByRetailer } from "@/lib/offerDefaults";
import ProductReviews from "@/components/ProductReviews";
import { PriceDisplay } from "@/components/PriceDisplay";
import { AmazonRelatedProducts, extractAsin } from "@/components/AmazonRelatedProducts";
import { MatchingReviewCTA } from "@/components/MatchingReviewCTA";

const BASE_URL = "https://www.garden-review.co.uk";

const chainsawTypeLabels: Record<string, string> = {
  standard: "Standard Chainsaw",
  top_handle: "Top-Handle Chainsaw",
  pole: "Pole Chainsaw",
  mini: "Mini Chainsaw",
  carving: "Carving Chainsaw",
};

const ChainsawProduct = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: chainsaw, isLoading } = useQuery({
    queryKey: ["chainsaw-product", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("chainsaws")
        .select("*")
        .eq("slug", slug)
        .eq("status", "published")
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const { data: affiliatesRaw = [] } = useQuery({
    queryKey: ["chainsaw-affiliates", chainsaw?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("chainsaw_affiliates")
        .select("*")
        .eq("chainsaw_id", chainsaw!.id)
        .order("price_gbp", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return data;
    },
    enabled: !!chainsaw?.id,
  });

  if (isLoading) {
    return <PageLoading />;
  }

  if (!chainsaw) {
    return (
      <>
        <Header />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Chainsaw Not Found</h1>
            <p className="text-muted-foreground mb-6">This chainsaw listing doesn't exist or hasn't been published yet.</p>
            <Link to="/best-chainsaws-uk"><Button>Browse All Chainsaws</Button></Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const title = `${chainsaw.brand} ${chainsaw.model} — Specs, Price & Where to Buy in the UK`;
  const metaDescription = chainsaw.description
    ? `${chainsaw.description.slice(0, 140)}…`
    : `${chainsaw.brand} ${chainsaw.model} — compare prices from UK retailers. ${chainsaw.best_for || "Find the best deal today."}`;
  const pageUrl = `${BASE_URL}/chainsaws/${chainsaw.slug}`;
  const affiliates = dedupeAffiliatesByRetailer(affiliatesRaw as any[]);
  const cheapest = affiliates.length > 0 ? affiliates[0] : null;
  const displayPrice = cheapest?.price_gbp || chainsaw.price_gbp;

  const productSchema = buildProductSchema(chainsaw as any, affiliates as any, { fallbackDescription: metaDescription });

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: BASE_URL },
      { "@type": "ListItem", position: 2, name: "Best Chainsaws UK", item: `${BASE_URL}/best-chainsaws-uk` },
      { "@type": "ListItem", position: 3, name: `${chainsaw.brand} ${chainsaw.model}`, item: pageUrl },
    ],
  };

  const specs = [
    { label: "Type", value: chainsawTypeLabels[chainsaw.chainsaw_type] || chainsaw.chainsaw_type },
    { label: "Power Type", value: chainsaw.power_type?.replace("_", " ") },
    ...(chainsaw.bar_length_cm ? [{ label: "Bar Length", value: `${chainsaw.bar_length_cm} cm` }] : []),
    ...(chainsaw.engine_cc ? [{ label: "Engine", value: `${chainsaw.engine_cc} cc` }] : []),
    ...(chainsaw.voltage ? [{ label: "Voltage", value: `${chainsaw.voltage}V` }] : []),
    ...(chainsaw.weight_kg ? [{ label: "Weight", value: `${chainsaw.weight_kg} kg` }] : []),
    ...(chainsaw.chain_speed_ms ? [{ label: "Chain Speed", value: `${chainsaw.chain_speed_ms} m/s` }] : []),
    ...(chainsaw.oil_tank_capacity_ml ? [{ label: "Oil Tank", value: `${chainsaw.oil_tank_capacity_ml} ml` }] : []),
    { label: "Auto Chain Tensioning", value: chainsaw.auto_chain_tensioning ? "Yes" : "No" },
    { label: "Tool-less Chain Change", value: chainsaw.tool_less_chain_change ? "Yes" : "No" },
    { label: "Anti-Vibration", value: chainsaw.anti_vibration ? "Yes" : "No" },
    { label: "Kickback Protection", value: chainsaw.kickback_protection ? "Yes" : "No" },
    ...(chainsaw.use_case?.length ? [{ label: "Best Use Cases", value: (chainsaw.use_case as string[]).map((u: string) => u.replace("_", " ")).join(", ") }] : []),
  ];

  const isThin = isThinProduct(chainsaw);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        {isThin && <meta name="robots" content="noindex,follow" />}
        {/* canonical is emitted server-side by the route head() */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="product" />
        {chainsaw.image_url && <meta property="og:image" content={chainsaw.image_url} />}
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{jsonLd(productSchema)}</script>
        <script type="application/ld+json">{jsonLd(breadcrumbSchema)}</script>
      </Helmet>

      <Header />
      <main className="max-w-5xl mx-auto px-4 py-8">
        <nav className="flex items-center gap-1 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/best-chainsaws-uk" className="hover:text-foreground">Best Chainsaws</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground font-medium">{chainsaw.brand} {chainsaw.model}</span>
        </nav>

        {/* Hero */}
        <div className="md:flex gap-8 mb-10">
          {chainsaw.image_url && (
            <div className="md:w-96 shrink-0 mb-6 md:mb-0">
              <div className="bg-muted rounded-xl overflow-hidden aspect-square flex items-center justify-center">
                <img src={chainsaw.image_url} alt={`${chainsaw.brand} ${chainsaw.model} chainsaw`} className="w-full h-full object-contain p-4" />
              </div>
            </div>
          )}
          <div className="flex-1">
            <p className="text-sm text-muted-foreground uppercase tracking-wide">{chainsaw.brand}</p>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">{chainsaw.brand} {chainsaw.model}</h1>
            {chainsaw.rating && (
              <div className="flex items-center gap-2 mb-3">
                <div className="flex items-center gap-1 bg-accent/10 text-accent px-3 py-1 rounded-full">
                  <Star className="h-4 w-4 fill-current" /><span className="font-bold">{chainsaw.rating}/5</span>
                </div>
              </div>
            )}
            {chainsaw.best_for && <Badge variant="secondary" className="mb-4 text-sm">{chainsaw.best_for}</Badge>}
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge variant="outline">{chainsawTypeLabels[chainsaw.chainsaw_type] || chainsaw.chainsaw_type}</Badge>
              <Badge variant="outline" className="capitalize">{chainsaw.power_type}</Badge>
              {chainsaw.bar_length_cm && <Badge variant="outline">{chainsaw.bar_length_cm}cm bar</Badge>}
              {chainsaw.engine_cc && <Badge variant="outline">{chainsaw.engine_cc}cc</Badge>}
              {chainsaw.voltage && <Badge variant="outline">{chainsaw.voltage}V</Badge>}
              {chainsaw.anti_vibration && <Badge variant="outline">Anti-vibration</Badge>}
            </div>
            {displayPrice && <p className="text-3xl font-bold text-foreground mb-1">From £{displayPrice.toLocaleString()}</p>}
            {chainsaw.price_range && <p className="text-sm text-muted-foreground mb-4 capitalize">{chainsaw.price_range} price range</p>}
            {cheapest ? (
              <a href={cheapest.affiliate_link} target="_blank" rel="noopener noreferrer sponsored nofollow">
                <Button size="lg" className="gap-2 text-base px-8"><ShoppingCart className="h-5 w-5" /> Buy from {cheapest.retailer_name} — £{cheapest.price_gbp?.toLocaleString()}</Button>
              </a>
            ) : chainsaw.affiliate_link ? (
              <a href={chainsaw.affiliate_link} target="_blank" rel="noopener noreferrer sponsored nofollow">
                <Button size="lg" className="gap-2 text-base px-8">
                  <ShoppingCart className="h-5 w-5" /> Buy from {chainsaw.affiliate_source || "Retailer"}
                  {chainsaw.price_gbp ? ` — £${chainsaw.price_gbp.toLocaleString()}` : ""}
                </Button>
              </a>
            ) : null}
          </div>
        </div>

        <MatchingReviewCTA brand={chainsaw.brand} model={chainsaw.model} externalUrl={(chainsaw as any).expert_review_url} />

        {/* Price Comparison */}
        {affiliates.length > 0 ? (
          <section className="mb-10">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" /> Compare Prices from {affiliates.length} UK {affiliates.length === 1 ? "Retailer" : "Retailers"}
            </h2>
            <div className="grid gap-3">
              {affiliates.map((aff, i) => (
                <a key={aff.id} href={aff.affiliate_link} target="_blank" rel="noopener noreferrer sponsored nofollow"
                  className={cn(
                    "flex items-center justify-between p-4 rounded-lg border text-sm transition-all hover:shadow-md",
                    aff.is_primary ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                      : i === 0 ? "border-primary/40 bg-primary/5" : "border-border hover:border-primary/30"
                  )}>
                  <span className="font-semibold text-base flex items-center gap-2">
                    {aff.retailer_name}
                    {i === 0 && <Badge className="text-[10px] py-0 px-1.5 bg-primary-foreground/20 text-primary-foreground border-primary-foreground/30">Cheapest</Badge>}
                  </span>
                  <span className="flex items-center gap-3">
                    <PriceDisplay price={aff.price_gbp} previousPrice={(aff as any).previous_price_gbp} availability={(aff as any).availability} lastChecked={aff.price_last_checked} />
                    <ExternalLink className={cn("h-4 w-4", aff.is_primary ? "text-primary-foreground/70" : "text-muted-foreground")} />
                  </span>
                </a>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">As an affiliate partner, we may earn from qualifying purchases.</p>
          </section>
        ) : chainsaw.affiliate_link ? (
          <section className="mb-10">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> Where to Buy</h2>
            <a href={chainsaw.affiliate_link} target="_blank" rel="noopener noreferrer sponsored nofollow"
              className="flex items-center justify-between p-4 rounded-lg border border-primary/40 bg-primary/5 text-sm transition-all hover:shadow-md">
              <span className="font-semibold text-base">{chainsaw.affiliate_source || "Buy Online"}</span>
              <span className="flex items-center gap-3">
                <PriceDisplay price={chainsaw.price_gbp} previousPrice={(chainsaw as any).previous_price_gbp} availability={(chainsaw as any).availability} lastChecked={chainsaw.price_last_checked} />
                <ExternalLink className="h-4 w-4 text-muted-foreground" />
              </span>
            </a>
          </section>
        ) : null}

        {chainsaw.description && (
          <section className="mb-10">
            <h2 className="text-xl font-bold text-foreground mb-3">Overview</h2>
            <p className="text-muted-foreground leading-relaxed">{chainsaw.description}</p>
          </section>
        )}


        {(chainsaw.key_features as string[])?.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xl font-bold text-foreground mb-4">Key Features</h2>
            <ul className="grid md:grid-cols-2 gap-3">
              {(chainsaw.key_features as string[]).map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-sm bg-muted/50 rounded-lg p-3">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" /><span>{feature}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Customer Reviews */}
        <ProductReviews productType="chainsaw" productId={chainsaw.id} productName={`${chainsaw.brand} ${chainsaw.model}`} />

        <AmazonRelatedProducts
          asin={extractAsin(affiliates.find(a => extractAsin(a.affiliate_link))?.affiliate_link) || extractAsin(chainsaw.affiliate_link)}
          keywords={`${chainsaw.brand} ${chainsaw.model} chainsaw`}
          searchIndex="All"
          productName={`${chainsaw.brand} ${chainsaw.model}`}
        />

        <section className="mb-10">
          <h2 className="text-xl font-bold text-foreground mb-4">Technical Specifications</h2>
          <Card><CardContent className="p-0">
            <table className="w-full text-sm"><tbody>
              {specs.map((spec, i) => (
                <tr key={i} className={cn("border-b last:border-0", i % 2 === 0 ? "bg-muted/30" : "")}>
                  <td className="px-4 py-3 font-medium text-foreground w-1/3">{spec.label}</td>
                  <td className="px-4 py-3 text-muted-foreground capitalize">{spec.value}</td>
                </tr>
              ))}
            </tbody></table>
          </CardContent></Card>
        </section>

        <div className="text-center border-t pt-8">
          <Link to="/best-chainsaws-uk">
            <Button variant="outline" className="gap-2"><ChevronRight className="h-4 w-4 rotate-180" /> Back to Chainsaw Guide</Button>
          </Link>
          <p className="text-xs text-muted-foreground mt-4">As an affiliate partner, we earn from qualifying purchases at no extra cost to you.</p>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default ChainsawProduct;
